import type { LiveCategory, LiveStatus } from "@/mocks/lives";
import { channelCategories } from "@/mocks/channels";

export interface ChannelFilterValue {
  category: LiveCategory | "all";
  status: LiveStatus | "all";
  sort: "hot" | "new";
}

interface FilterBarProps {
  value: ChannelFilterValue;
  onChange: (next: Partial<ChannelFilterValue>) => void;
  onReset: () => void;
}

const statusOptions: { value: LiveStatus | "all"; label: string }[] = [
  { value: "all", label: "全部状态" },
  { value: "live", label: "直播中" },
  { value: "upcoming", label: "预告" },
];

const sortOptions: { value: "hot" | "new"; label: string }[] = [
  { value: "hot", label: "热门度" },
  { value: "new", label: "最新上线" },
];

function pillClass(active: boolean) {
  return `whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
    active
      ? "bg-primary-500 text-background-50"
      : "border border-background-200 bg-background-100 text-foreground-700 hover:border-primary-500/60 hover:text-foreground-950"
  }`;
}

export default function FilterBar({ value, onChange, onReset }: FilterBarProps) {
  const categoryOptions: { value: LiveCategory | "all"; label: string }[] = [
    { value: "all", label: "全部频道" },
    ...channelCategories.map((channel) => ({ value: channel.key, label: channel.name })),
  ];

  return (
    <div className="rounded-lg border border-background-200/60 bg-background-100 p-5">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <span className="w-20 shrink-0 text-sm font-medium text-foreground-500">频道类型</span>
          <div className="flex flex-wrap gap-2">
            {categoryOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ category: option.value })}
                className={pillClass(value.category === option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="h-px w-full bg-background-200/60" />

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <span className="w-20 shrink-0 text-sm font-medium text-foreground-500">直播状态</span>
          <div className="flex flex-wrap gap-2">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ status: option.value })}
                className={pillClass(value.status === option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="h-px w-full bg-background-200/60" />

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <span className="w-20 shrink-0 text-sm font-medium text-foreground-500">排序方式</span>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 rounded-full bg-background-200/70 px-1 py-1">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onChange({ sort: option.value })}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    value.sort === option.value
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={onReset}
              className="ml-auto inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-600 transition-colors hover:text-primary-300"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}